import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { createEmployeeNavSections } from '../components/layout/dashboardNavConfigs';
import TimeOffRequestModal from '../components/timeoff/TimeOffRequestModal';
import { useLocation } from 'react-router-dom';
import { timeOffRequestsAPI, timeOffBalancesAPI } from '../services/api';

interface TimeOffRequest {
  id: number;
  request_type: string;
  start_date: string;
  end_date: string;
  total_days: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  rejection_reason?: string;
}

interface TimeOffBalance {
  vacation: number;
  sick: number;
  personal: number;
}

const MyTimeOff: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [requests, setRequests] = useState<TimeOffRequest[]>([]);
  const [balances, setBalances] = useState<TimeOffBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState<TimeOffRequest | null>(
    null,
  );

  const isActive = (href: string) => {
    if (href === '/employee-dashboard')
      return location.pathname === '/employee-dashboard';
    return location.pathname.startsWith(href);
  };

  const sections = createEmployeeNavSections(t, isActive);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestsRes, balancesRes] = await Promise.all([
        timeOffRequestsAPI.getAll(),
        timeOffBalancesAPI.getMyBalance(),
      ]);

      const userRequests = requestsRes.data.time_off_requests.filter(
        (req: any) => req.user?.id === user?.id || req.user_id === user?.id,
      );

      setRequests(userRequests);
      setBalances(balancesRes);
    } catch (error) {
      showToast('Failed to load time off data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user?.id]);

  const handleDelete = async (id: number) => {
    if (window.confirm(t('timeOff.deleteConfirm'))) {
      try {
        await timeOffRequestsAPI.delete(id);
        showToast('Request deleted successfully', 'success');
        fetchData();
      } catch (error) {
        showToast('Failed to delete request', 'error');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      vacation: t('timeOff.vacation'),
      sick: t('timeOff.sickLeave'),
      personal: t('timeOff.personal'),
      emergency: t('timeOff.emergency'),
    };
    return typeMap[type] || type;
  };

  const content = (
    <>
      {/* Sticky PageHeader - Same as TimeTracking */}
      <div
        className={`border-b ${isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-white/80 border-slate-200'} backdrop-blur-sm sticky top-0 z-10`}
      >
        <div className="px-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 py-5">
            <div>
              <h1
                className={`text-xl sm:text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {t('timeOff.myRequests')}
              </h1>
              <p
                className={`mt-0.5 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('dashboard.subtitle')}
              </p>
            </div>
            <div className="w-full sm:w-auto">
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>{t('timeOff.newRequest')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        <div className="p-6">
          {/* Balance Cards */}
          {balances && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <BalanceCard
                label={t('timeOff.vacation')}
                days={balances.vacation}
                isDark={isDark}
                color="indigo"
              />
              <BalanceCard
                label={t('timeOff.sickLeave')}
                days={balances.sick}
                isDark={isDark}
                color="red"
              />
              <BalanceCard
                label={t('timeOff.personal')}
                days={balances.personal}
                isDark={isDark}
                color="blue"
              />
            </div>
          )}

          {/* Requests List Card */}
          <div
            className={`rounded-2xl border p-6 ${
              isDark
                ? 'bg-slate-900/50 border-slate-700/50'
                : 'bg-white border-slate-200'
            }`}
          >
            {loading ? (
              <div
                className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
              >
                Loading...
              </div>
            ) : requests.length === 0 ? (
              <div
                className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
              >
                {t('timeOff.noRequests')}
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      isDark
                        ? 'bg-slate-700 border-slate-600'
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className={`font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                        >
                          {getTypeLabel(request.request_type)}
                        </span>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(request.status)}`}
                        >
                          {request.status}
                        </span>
                      </div>
                      <div
                        className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                      >
                        {request.start_date} {t('timeOff.to')}{' '}
                        {request.end_date}
                      </div>
                      <div
                        className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                      >
                        {request.total_days} {t('timeOff.days')}
                      </div>
                      {request.reason && (
                        <div
                          className={`text-sm mt-2 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                        >
                          {request.reason}
                        </div>
                      )}
                      {request.status === 'rejected' &&
                        request.rejection_reason && (
                          <div
                            className={`text-sm italic mt-1 ${isDark ? 'text-red-400' : 'text-red-500'}`}
                          >
                            Reason: {request.rejection_reason}
                          </div>
                        )}
                    </div>
                    <div className="ml-4 flex gap-2">
                      {request.status === 'pending' && (
                        <button
                          onClick={() => handleDelete(request.id)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-all"
                        >
                          {t('timeOff.cancel')}
                        </button>
                      )}
                      {(request.status === 'approved' ||
                        request.status === 'rejected') && (
                        <button
                          onClick={() => {
                            setEditingRequest(request);
                            setShowModal(true);
                          }}
                          className="px-3 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg text-sm font-medium transition-all"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <TimeOffRequestModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingRequest(null);
        }}
        onSuccess={fetchData}
        requestToEdit={editingRequest}
      />
    </>
  );

  return <DashboardLayout sections={sections}>{content}</DashboardLayout>;
};

interface BalanceCardProps {
  label: string;
  days: number;
  isDark: boolean;
  color: 'indigo' | 'red' | 'blue';
}

const BalanceCard: React.FC<BalanceCardProps> = ({
  label,
  days,
  isDark,
  color,
}) => {
  const colorMap = {
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
  };

  const darkColorMap = {
    indigo: 'bg-indigo-900/20 border-indigo-700 text-indigo-400',
    red: 'bg-red-900/20 border-red-700 text-red-400',
    blue: 'bg-blue-900/20 border-blue-700 text-blue-400',
  };

  const styleClass = isDark ? darkColorMap[color] : colorMap[color];

  return (
    <div className={`rounded-lg border p-4 ${styleClass}`}>
      <p className="text-sm font-medium">{label}</p>
      <p className="text-3xl font-bold mt-2">{days}</p>
      <p className="text-xs opacity-75 mt-1">days remaining</p>
    </div>
  );
};

export default MyTimeOff;
